import {
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { NgbDatepickerI18n } from '@ng-bootstrap/ng-bootstrap';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { CommonService } from '@geneplanet/ngx-shop/src/lib/shared';

import { Auth0Module } from '@geneplanet/ngx-auth0';

import { environment } from 'src/environments/environment';

import { MultipleFilesHttpTranslationsLoader } from './multiple-files-http-translations-loader';

import * as Sentry from '@sentry/angular';

import { RecaptchaV3Module, RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha-2';
import { COOKIE_DOMAIN, COOKIE_LANG, RECAPTCHA } from 'src/app/app.constants';
import { buildInfo } from 'src/environments/buildInfo';
import {
  CacheMechanism,
  LocalizeParser,
  LocalizeRouterService,
  LocalizeRouterSettings,
  withLocalizeRouter,
} from '@gilsdav/ngx-translate-router';
import { LanguageService } from './shared/services/language.service';
import {
  AnalyticsService as Analytics,
  AnalyticsModule,
  withRudderstack,
} from '@geneplanet/ngx-utils/src/lib/analytics';
import {
  catchError,
  filter,
  first,
  map,
  mapTo,
  tap,
  timeout,
} from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';
import {
  NavigationStart,
  provideRouter,
  Router,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { UserIntentState } from './shared/interfaces/intent.interface';
import { IntentStrategyService } from './shared/services/intent/intent-strategy.service';
import { IntentService } from './shared/services/intent/intent.service';
import { CustomPreloadingStrategy } from './core/utils/custom-preload-strategy';
import { NgbDatepickerI18nCustom } from './core/utils/custom-datepicker-i18n';
import {
  provideLokalise,
  withEnvironmentEnabled,
  withProjectId,
} from './lokalise';
import { HammerModule } from '@angular/platform-browser';
import { routes } from './app.routes';
import { ManualLoaderFactory } from './app.utils';
import { CustomTranslateService } from './core/services/custom-translate.service';
import { Location } from '@angular/common';
import { httpInterceptorProviders } from './core/interceptors';
import { GpuToastModule } from '@geneplanet/ngx-ui';
import { provideAnimations } from '@angular/platform-browser/animations';
import { LayoutModule } from '@angular/cdk/layout';
import { provideNgxUtils } from '@geneplanet/ngx-utils/src/lib/shared';
import { withTrustpilot } from '@geneplanet/ngx-utils/src/lib/trustpilot';
import { LocationCode } from '@geneplanet/ngx-utils/src/lib/localization';
import { versionInfo } from 'src/app/version-info';
import { provideNgxStripe } from 'ngx-stripe';

const appInitializerFactory = () => {
  const localize = inject(LocalizeRouterService);
  const common = inject(CommonService);
  const language = inject(LanguageService);
  const intentService = inject(IntentService);
  const intentStrategyService = inject(IntentStrategyService);
  const router = inject(Router);
  const analyticsService = inject(Analytics);

  const localizeInitialized = localize.hooks.initialized.pipe(
    map(() => {
      const location = common.getLocationFromCode(localize.parser.currentLang);
      return language.onLocationChange(location);
    })
  );

  // magic link page track
  router.events
    .pipe(
      filter((event) => event instanceof NavigationStart),
      first()
    )
    .subscribe((event: NavigationStart) => {
      if (
        analyticsService.isInitialized &&
        event.url.startsWith('/nipt/view/')
      ) {
        analyticsService.pageTrack(event.url);
      }
    });

  const intentInitialized = (): Observable<boolean> => {
    return intentService.fetchUserIntent().pipe(
      tap((state: UserIntentState) => {
        intentStrategyService.setStrategyByIntentId(state.intentId);
      }),
      mapTo(true),
      // Make sure API call doesn't block application bootstrap too long
      timeout(4000),
      // If Intent API call fails, whole application will not load.
      catchError((exception: any) => {
        if (exception?.name === 'TimeoutError') {
          Sentry.captureException(`[INTENT BOOTSTRAP] Request timeout!`);
        } else {
          Sentry.captureException(
            `[INTENT BOOTSTRAP] Exception: ${exception?.message ?? 'UNKNOWN!!'}`
          );
        }
        return of(true);
      })
    );
  };

  return combineLatest([localizeInitialized, intentInitialized()]).toPromise();
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideRouter(routes),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    importProvidersFrom(
      Auth0Module.forRoot(environment),
      RecaptchaV3Module,
      AnalyticsModule,
      HammerModule,
      GpuToastModule,
      LayoutModule
    ),
    provideRouter(
      routes,
      withPreloading(CustomPreloadingStrategy),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
      withComponentInputBinding(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
        paramsInheritanceStrategy: 'always',
      }),
      withLocalizeRouter(routes, {
        parser: {
          provide: LocalizeParser,
          useFactory: ManualLoaderFactory,
          deps: [CustomTranslateService, Location, LocalizeRouterSettings],
        },
        defaultLangFunction: (languages, cachedLang, browserLang) => {
          const locationCode = cachedLang || browserLang;
          if (!languages.includes(locationCode)) {
            return languages.find((i) => i === LocationCode.EU);
          }

          return locationCode;
        },
        cacheName: COOKIE_LANG,
        cacheMechanism: CacheMechanism.Cookie,
        cookieFormat: `{{value}};{{expires}};path=/;domain=${COOKIE_DOMAIN}`,
        useCachedLang: true,
        alwaysSetPrefix: false,
      })
    ),
    LocalizeRouterService,
    httpInterceptorProviders,
    CustomPreloadingStrategy,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: RECAPTCHA.siteKey },
    { provide: 'environment', useValue: environment },
    provideAppInitializer(appInitializerFactory),
    { provide: NgbDatepickerI18n, useClass: NgbDatepickerI18nCustom },
    { provide: 'googleTagManagerId', useValue: environment.gtmContainer },
    provideAnimations(),
    provideLokalise(
      withProjectId('73322359621e0324812c59.72472535'),
      withEnvironmentEnabled(environment.environment !== 'production')
    ),
    provideNgxUtils(
      withTrustpilot('5d37170f9bfa5000013362b3', '5419b732fbfb950b10de65e5', {
        theme: 'dark',
        fontFamily: 'Open Sans',
      }),
      withRudderstack({
        enabled: environment.rudderStackEnabled,
        application: 'HI',
        writeKey: environment.rudderstackKey,
        dataPlaneUrl: 'https://geneplanetympr.dataplane.rudderstack.com',
        buildDate: parseInt(versionInfo.buildNumber, 10),
      })
    ),
    provideNgxStripe(),
  ],
};

export function createTranslateLoader(http: HttpClient) {
  // Add version suffix to translation files, to make sure
  // latest translations are loaded
  const buildSuffix =
    String(buildInfo.buildNumber) !== 'dev'
      ? `?version=${buildInfo.buildNumber}`
      : '';
  return new MultipleFilesHttpTranslationsLoader(http, [
    { prefix: `./assets/i18n/`, suffix: `.json${buildSuffix}` },
    { prefix: `./assets/i18n/shop/`, suffix: `.json${buildSuffix}` },
  ]);
}
